/**
 * SVG-кружок с инициалами для маркера инспектора на dispatch-карте.
 * Зелёный — свободен и свежая позиция; жёлтый — устаревшая (но онлайн);
 * серый — занят активным заказом.
 */
import i18n from '@/utils/i18n'

export type InspectorMarkerTone = 'free-fresh' | 'free-stale' | 'busy'

const TONE_COLOR: Record<InspectorMarkerTone, { bg: string; ring: string }> = {
  'free-fresh': { bg: '#22c55e', ring: '#0d3a17' },
  'free-stale': { bg: '#eab308', ring: '#3b2c05' },
  'busy':       { bg: '#94a3b8', ring: '#1f2937' },
}

function initialsOf(name: string | null): string {
  if (!name) return '?'
  const parts = name.trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return '?'
  if (parts.length === 1) return parts[0].slice(0, 1).toUpperCase()
  return (parts[0][0] + parts[1][0]).toUpperCase()
}

export function inspectorMarkerDataUrl(
  fullName: string | null,
  tone: InspectorMarkerTone,
): string {
  const c = TONE_COLOR[tone]
  const initials = initialsOf(fullName)
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32">
  <circle cx="16" cy="16" r="13" fill="${c.bg}" stroke="${c.ring}" stroke-width="2"/>
  <text x="16" y="20" text-anchor="middle" font-family="Inter, system-ui, sans-serif" font-size="11" font-weight="700" fill="#0d0f14">${initials}</text>
</svg>`
  const b64 = btoa(unescape(encodeURIComponent(svg)))
  return `data:image/svg+xml;base64,${b64}`
}

export function inspectorTone(
  busy: boolean,
  isFresh: boolean,
): InspectorMarkerTone {
  if (busy) return 'busy'
  return isFresh ? 'free-fresh' : 'free-stale'
}

/** Подпись для midpoint полилинии — числовая в км. */
export function distanceLabelDataUrl(km: number): string {
  const text = i18n.t('geo.map.distanceKm', { km: km.toFixed(1) })
  // Размер подгоняем под длину текста.
  const w = Math.max(48, 8 + text.length * 7)
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="20" viewBox="0 0 ${w} 20">
  <rect x="0" y="0" width="${w}" height="20" rx="6" ry="6" fill="rgba(15,17,21,0.92)" stroke="rgba(255,255,255,0.18)" stroke-width="1"/>
  <text x="${w / 2}" y="14" text-anchor="middle" font-family="Inter, system-ui, sans-serif" font-size="11" font-weight="600" fill="#f3f4f6">${text}</text>
</svg>`
  const b64 = btoa(unescape(encodeURIComponent(svg)))
  return `data:image/svg+xml;base64,${b64}`
}

/** Расстояние по гаверсинусу в км. */
export function haversineKm(
  a: { lat: number; lon: number },
  b: { lat: number; lon: number },
): number {
  const R = 6371
  const toRad = (d: number) => (d * Math.PI) / 180
  const dLat = toRad(b.lat - a.lat)
  const dLon = toRad(b.lon - a.lon)
  const sa =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLon / 2) ** 2
  const c = 2 * Math.atan2(Math.sqrt(sa), Math.sqrt(1 - sa))
  return R * c
}
